import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Home, MapPin, Search, Loader2, Trash2, Plus, ArrowLeft, Building, Eye, ExternalLink, Sparkles, Pencil, Star } from 'lucide-react';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';

interface TownData {
  id: string;
  name: string;
  state: string | null;
  slug: string;
  is_published: boolean;
  created_at: string;
  _count?: {
    areas: number;
    listings: number;
  };
}

export default function TownManage() {
  const { user, loading: authLoading } = useAuth();
  const { selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [defaultTownId, setDefaultTownId] = useState<string | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TownData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [togglingPublish, setTogglingPublish] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchTowns();
    }
  }, [selectedSiteId]);

  const fetchTowns = async () => {
    if (!selectedSiteId) return;
    
    setLoading(true);
    try {
      // Get site default town
      const { data: siteData } = await supabase
        .from('sites')
        .select('default_town_id')
        .eq('id', selectedSiteId)
        .single() as { data: { default_town_id: string | null } | null; error: any };

      if (siteData) {
        setDefaultTownId(siteData.default_town_id);
      }

      // Get towns with counts
      const { data: townData } = await supabase
        .from('towns')
        .select('id, name, state, slug, is_published, created_at')
        .eq('site_id', selectedSiteId)
        .order('name');

      if (townData) {
        // Get area counts for each town
        const townsWithCounts = await Promise.all(
          townData.map(async (town) => {
            const [areasRes, listingsRes] = await Promise.all([
              supabase.from('areas').select('id', { count: 'exact', head: true }).eq('town_id', town.id),
              supabase.from('listings').select('id', { count: 'exact', head: true }).eq('town_id', town.id),
            ]);
            return {
              ...town,
              _count: {
                areas: areasRes.count || 0,
                listings: listingsRes.count || 0,
              },
            };
          })
        );
        setTowns(townsWithCounts);
      }
    } catch (error) {
      console.error('Error fetching towns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-town', {
        body: { townId: deleteTarget.id },
      });

      if (error) throw error;

      setTowns(towns.filter((t) => t.id !== deleteTarget.id));
      toast({
        title: 'Town deleted',
        description: `${deleteTarget.name} has been removed (and ${data?.deletedAreasCount ?? 0} associated area(s) deleted).`,
      });

      // Re-fetch to ensure UI matches backend state (prevents “reappears later”)
      fetchTowns();
    } catch (error: any) {
      console.error('Error deleting town:', error);
      toast({
        title: 'Error deleting town',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const regenerateContent = async (town: TownData, withImages = true) => {
    setRegenerating(town.id);
    try {
      const { data, error } = await supabase.functions.invoke('generate-town-content', {
        body: { townIds: [town.id], generateImages: withImages },
      });

      if (error) throw error;

      toast({
        title: 'Content regeneration started!',
        description: `AI content ${withImages ? 'and images ' : ''}for ${town.name} is being generated. This may take a few minutes.`,
      });
    } catch (error: any) {
      console.error('Error regenerating content:', error);
      toast({
        title: 'Error regenerating content',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setRegenerating(null);
    }
  };

  const regenerateAllWithImages = async () => {
    setRegeneratingAll(true);
    
    toast({
      title: 'Regenerating all content with images',
      description: `Processing ${towns.length} towns. This will take several minutes.`,
    });

    // Process towns one by one to avoid timeouts
    for (const town of towns) {
      try {
        console.log(`Regenerating content for ${town.name}...`);
        await supabase.functions.invoke('generate-town-content', {
          body: { townIds: [town.id], generateImages: true },
        });
        console.log(`Completed ${town.name}`);
      } catch (error) {
        console.error(`Error regenerating ${town.name}:`, error);
      }
    }

    toast({
      title: 'Regeneration complete!',
      description: 'All towns have been processed. Refresh the page to see updates.',
    });
    
    setRegeneratingAll(false);
  };

  const setAsDefaultHome = async (town: TownData) => {
    if (!selectedSiteId) return;
    
    setSettingDefault(town.id);
    try {
      // If already default, remove it
      const newDefaultId = defaultTownId === town.id ? null : town.id;
      
      const { error } = await supabase
        .from('sites')
        .update({ default_town_id: newDefaultId } as any)
        .eq('id', selectedSiteId);

      if (error) throw error;

      setDefaultTownId(newDefaultId);
      toast({
        title: newDefaultId ? 'Default home page set!' : 'Default removed',
        description: newDefaultId 
          ? `${town.name} is now your home page.`
          : 'Visitors will see the towns list as the home page.',
      });
    } catch (error: any) {
      console.error('Error setting default:', error);
      toast({
        title: 'Error setting default',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSettingDefault(null);
    }
  };

  const togglePublish = async (town: TownData) => {
    setTogglingPublish(town.id);
    try {
      const newStatus = !town.is_published;
      
      const { error } = await supabase
        .from('towns')
        .update({ is_published: newStatus })
        .eq('id', town.id);

      if (error) throw error;

      setTowns(towns.map(t => 
        t.id === town.id ? { ...t, is_published: newStatus } : t
      ));
      
      // Auto-trigger IndexNow when town is published
      if (newStatus && selectedSiteId) {
        supabase.functions.invoke('auto-indexnow', {
          body: { siteId: selectedSiteId, trigger: 'town_published', entityId: town.id }
        }).then(({ error }) => {
          if (error) {
            console.error('Auto-IndexNow error:', error);
          } else {
            console.log('Auto-IndexNow triggered for town publish');
          }
        });
      }
      
      toast({
        title: newStatus ? 'Town published!' : 'Town unpublished',
        description: newStatus 
          ? `${town.name} is now live and visible to visitors.`
          : `${town.name} is now hidden from visitors.`,
      });
    } catch (error: any) {
      console.error('Error toggling publish:', error);
      toast({
        title: 'Error updating town',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setTogglingPublish(null);
    }
  };

  const filteredTowns = towns.filter(town =>
    town.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (town.state && town.state.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <MapPin className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">Manage Towns</span>
            </div>
            <div className="flex items-center gap-4">
              <SiteSwitcher />
              <Button variant="gold" onClick={() => navigate('/admin/towns/setup')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Towns
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-display font-bold">Your Towns</h1>
          <p className="text-muted-foreground mt-1">
            {towns.length} town{towns.length !== 1 ? 's' : ''} configured
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search towns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Towns List */}
        {filteredTowns.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No towns yet</h3>
              <p className="text-muted-foreground mb-4">
                Add towns to start building your real estate site.
              </p>
              <Button variant="gold" onClick={() => navigate('/admin/towns/setup')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Towns
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredTowns.map((town) => (
              <Card key={town.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${town.is_published ? 'bg-estate-success' : 'bg-amber-500'}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{town.name}</h3>
                          {town.state && (
                            <span className="text-sm text-muted-foreground">{town.state}</span>
                          )}
                          {defaultTownId === town.id && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                              <Star className="h-3 w-3 fill-current" />
                              Home Page
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {town._count?.areas || 0} areas
                          </span>
                          <span className="flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            {town._count?.listings || 0} listings
                          </span>
                          <span className={town.is_published ? 'text-estate-success' : 'text-amber-600'}>
                            {town.is_published ? 'Published' : 'Draft'}
                          </span>
                          <Switch
                            checked={town.is_published}
                            onCheckedChange={() => togglePublish(town)}
                            disabled={togglingPublish === town.id}
                            aria-label={town.is_published ? 'Unpublish town' : 'Publish town'}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={defaultTownId === town.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAsDefaultHome(town)}
                        disabled={settingDefault === town.id}
                        title={defaultTownId === town.id ? "Click to remove as home page" : "Set as home page"}
                      >
                        {settingDefault === town.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Star className={`h-4 w-4 ${defaultTownId === town.id ? 'fill-current' : ''}`} />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/towns/${town.id}/content`)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => regenerateContent(town)}
                        disabled={regenerating === town.id}
                      >
                        {regenerating === town.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-1" />
                        )}
                        {regenerating === town.id ? 'Generating...' : 'Regenerate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/${town.slug}?preview=true`, '_blank')}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      {town.is_published && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/${town.slug}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteTarget(town)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {towns.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate('/admin/towns/setup')}>
                <Plus className="h-4 w-4 mr-2" />
                Add More Towns
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/areas/setup')}>
                <Building className="h-4 w-4 mr-2" />
                Add Areas
              </Button>
              <Button 
                variant="gold" 
                onClick={regenerateAllWithImages}
                disabled={regeneratingAll}
              >
                {regeneratingAll ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Regenerating All...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Regenerate All With Images
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this town and all its associated areas.
              {(deleteTarget?._count?.areas || 0) > 0 && (
                <span className="block mt-2 font-medium text-destructive">
                  Warning: This will also delete {deleteTarget?._count?.areas} area{(deleteTarget?._count?.areas || 0) !== 1 ? 's' : ''}.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}